import api from "./api.service";
import { tokenService } from "./token.service";

export interface AddressSubject {
  id?: number;
  idCustomerAddressBook?: number;
  firstName: string;
  lastName: string;
  kindOfDNI: string;
  dni: string;
  phoneNumber: string;
  email: string;
  isThirdPerson?: boolean;
  razonSocial?: string | null;
}

export interface Address {
  id: number;
  idCustomer?: number;
  nickname: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  isO2Country: string;
  status?: boolean;
  type: string;
  isDefault?: boolean;
  addressSubjet?: AddressSubject;
}

export interface AddAddress {
  nickname: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  isO2Country: string;
  type: string;
  isDefault?: boolean;
  addressSubjet?: AddressSubject;
}

export interface CustomerAddressesResponse {
  origins: Address[][];
  destinations: Address[][];
  all: Address[];
}

export const customerAddressService = {
  getAddresses: async (): Promise<CustomerAddressesResponse> => {
    try {
      const token = tokenService.getToken();
      if (!token) {
        throw new Error("No authentication token found.");
      }

      const response = await api.get("/customer-get-address", {
        headers: {
          Authorization: `${token}`,
        },
      });

      const data = response.data?.data;
      if (!data) {
        return { origins: [], destinations: [], all: [] };
      }

      const mapAddressItem = (item: any): Address => {
        const addr = item.address || item || {};
        const subject = item.subject || addr.addressSubjet || item.addressSubjet || null;

        return {
          ...addr,
          id: addr.id,
          nickname: addr.nickname || addr.addressLine1 || "Sin Nombre",
          addressLine1: addr.addressLine1 || "",
          addressLine2: addr.addressLine2 || "",
          city: addr.city || "",
          state: addr.state || "",
          zipCode: addr.zipCode || "",
          isO2Country: addr.isO2Country || addr.iso2Country || "",
          type: addr.type || "",
          isDefault: Boolean(addr.isDefault ?? addr.IsDefault ?? false),
          addressSubjet: subject
        };
      };

      let origins: Address[][] = [];
      let destinations: Address[][] = [];

      if (Array.isArray(data.origins)) {
        origins = data.origins.map((group: any[]) =>
          Array.isArray(group) ? group.map(mapAddressItem) : []
        );
      }

      if (Array.isArray(data.destinations)) {
        destinations = data.destinations.map((group: any[]) =>
          Array.isArray(group) ? group.map(mapAddressItem) : []
        );
      }

      // Fallback if legacy flat array is returned
      if (Array.isArray(data)) {
        const flatList = data.map(mapAddressItem);
        const originsMap: { [country: string]: Address[] } = {};
        const destsMap: { [country: string]: Address[] } = {};

        flatList.forEach((addr) => {
          const country = (addr.isO2Country || "").toUpperCase();
          if (addr.type === "O") {
            if (!originsMap[country]) originsMap[country] = [];
            originsMap[country].push(addr);
          } else if (addr.type === "D") {
            if (!destsMap[country]) destsMap[country] = [];
            destsMap[country].push(addr);
          }
        });

        origins = Object.values(originsMap);
        destinations = Object.values(destsMap);
      }

      const all = [...origins.flat(), ...destinations.flat()];

      return { origins, destinations, all };
    } catch (error) {
      console.error("Error fetching customer addresses:", error);
      throw error;
    }
  },

  addAddress: async (
    address: AddAddress
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = tokenService.getToken();
      if (!token) {
        throw new Error("No authentication token found.");
      }

      let payload = {
        nickname: address.nickname,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
        isO2Country: address.isO2Country,
        type: address.type,
        isDefault: address.isDefault,
        addressSubjet: address.addressSubjet
      };

      const response = await api.post("/customer-create-address", payload, {
        headers: {
          Authorization: `${token}`,
        },
      });
      if (response.data.statusCode === 200) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.response.data.errorDetail,
        };
      }
    } catch (error: any) {
      console.error("Error adding customer address:", error);
      return {
        success: false,
        error: error.response?.data?.errorDetail || error.message,
      };
    }
  },

  deleteAddress: async (
    addressId: number
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = tokenService.getToken();
      if (!token) {
        throw new Error("No authentication token found.");
      }

      let payload = {
        id: addressId,
      };

      const response = await api.post("/customer-delete-address", payload, {
        headers: {
          Authorization: `${token}`,
        },
      });

      if (response.data.statusCode === 200) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.response.data.errorDetail,
        };
      }
    } catch (error: any) {
      console.error("Error deleting customer address:", error);
      return {
        success: false,
        error: error.response?.data?.errorDetail || error.message,
      };
    }
  },

  updateAddress: async(
    address: Address
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = tokenService.getToken();
      if (!token) {
        throw new Error("No authentication token found.");
      }

      let payload = {
        id: address.id,
        nickname: address.nickname,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
        isO2Country: address.isO2Country,
        type: address.type,
        isDefault: address.isDefault,
        addressSubjet: address.addressSubjet
      };

      const response = await api.post("/customer-update-address", payload, {
        headers: {
          Authorization: `${token}`,
        },
      });

      if (response.data.statusCode === 200) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.response.data.errorDetail,
        };
      }
    } catch (error: any) {
      console.error("Error updating customer address:", error);
      return {
        success: false,
        error: error.response?.data?.errorDetail || error.message,
      };
    }
  },

  setDefaultAddress: async (
    addressId: number
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = tokenService.getToken();
      if (!token) {
        throw new Error("No authentication token found.");
      }

      const response = await api.post(
        "/customer-set-default-address",
        { idAddress: addressId },
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.data.statusCode === 200) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.errorDetail || response.data.message,
        };
      }
    } catch (error: any) {
      console.error("Error setting default address:", error);
      return {
        success: false,
        error: error.response?.data?.errorDetail || error.message,
      };
    }
  }
};
